import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { fetchInitiative } from "@/lib/api";

export default function InitiativeDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchInitiative(slug).then(setItem).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="ra-serif text-4xl">Initiative not found</h1>
        <Link to="/initiatives" className="ra-link mt-6 inline-block">← Back to initiatives</Link>
      </div>
    );
  }

  if (!item) return <div className="max-w-3xl mx-auto px-6 py-32"><div className="h-3 w-32 bg-ra-line animate-pulse"></div></div>;

  return (
    <div data-testid={`page-initiative-${item.slug}`}>
      <section className="border-b border-ra-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-12 pb-20 lg:pt-16 lg:pb-28">
          <Link to="/initiatives" data-testid="initiative-back" className="ra-overline inline-flex items-center gap-2 hover:text-ra-eucalypt transition-colors">
            <ArrowLeft size={14} /> Back to initiatives
          </Link>

          <div className="grid grid-cols-12 gap-10 mt-10 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div className="ra-overline">Initiative</div>
              <h1 className="ra-serif mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                {item.name}
              </h1>
              <p className="mt-6 italic text-2xl text-ra-ochre">{item.tagline}</p>
            </div>
            <div className="col-span-12 lg:col-span-4 lg:text-right">
              <div className="ra-overline">Status</div>
              <div className="ra-serif text-3xl mt-2 capitalize">{item.status}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-7">
          <div className="aspect-[16/10] overflow-hidden border border-ra-line">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          </div>
          <p className="mt-10 text-lg lg:text-xl ra-serif leading-relaxed text-ra-eucalypt">
            {item.description}
          </p>
        </div>

        <aside className="col-span-12 lg:col-span-5 lg:pl-10 lg:border-l border-ra-line">
          <div className="ra-overline">What we deliver</div>
          <ul className="mt-6 space-y-5">
            {item.highlights?.map((h, idx) => (
              <li key={idx} data-testid={`initiative-highlight-${idx}`} className="flex gap-4 items-start pb-5 border-b border-ra-line last:border-b-0">
                <Check size={20} className="text-ra-ochre flex-shrink-0 mt-1" strokeWidth={1.6} />
                <span className="text-ra-eucalypt leading-relaxed">{h}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 p-6 border border-ra-line bg-ra-paper">
            <div className="ra-overline">Get involved</div>
            <p className="mt-3 text-ra-soft leading-relaxed text-sm">
              Interested in partnering on {item.name}? Speak with our team about projects, partnerships, or training.
            </p>
            <Link to="/contact" data-testid="initiative-contact-cta" className="ra-btn-primary mt-5">
              Contact the team
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
